import Modal from 'antd/es/modal';
import queryString from 'query-string';
import React, { Suspense, useCallback, useEffect, useMemo } from 'react';
import { Provider, useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router';
import { AppProvider, useIsMobile } from 'src/app/providers/AppProvider';
import { plausibleEventHandler, PlausibleEventType } from 'src/helpers/plausibleEventHandler';
import { getToken, isTokenExist, setToken } from 'src/helpers/token';
import '../assets/styles/custom/style.sass';
import Loader from '../components/Loader';
import TapPageVarSnippets, { MatomoScript, UserWayScript } from '../components/TapPageVarSnippets';
import { ABTestNamesEnum, getAbTestBucket } from '../helpers/AbTest';
import { authBySpeakerCode } from '../helpers/externalSpeakerAuth';
import {
    extractIdFromUrlState,
    getCommonRoutePath,
    getUniversityId,
    isGlobalVersionTwo,
    isInIframe,
    isUniGroup,
} from '../helpers/Helpers';
import { getLeadTypePath } from '../helpers/leadTypeRouting';
import { NavigateWrapper } from '../helpers/navigateWrapper';
import { localStorageWrapper, sessionStorageWrapper } from '../helpers/storageFactory';
import {
    ACCOUNT_LOGOUT,
    AUTH_BY_CODE,
    getAccountInfo,
    LOGIN_SUCCESS,
    loginSuccess,
    MOB_APP_AUTH,
    SET_SLUG_FROM_URL,
    startRegistration,
} from '../redux/actions/accountActions';
import {
    CLOSE_ACTION_MODAL,
    CLOSE_ACTION_MODAL_CHILD,
    SET_MULTI_DATA_REGISTER_MODAL,
} from '../redux/actions/componentsActions';
import { MAIN_INFO } from '../redux/actions/mainActions';
import { setSearchTerm } from '../redux/actions/searchActions';
import {
    popcardHideFilters,
    SET_UTM_DATA,
    setTitles,
    SOCIAL_PROOF,
    WIDGET_INFO,
    WIDGET_LOAD,
} from '../redux/actions/widgetActions';
import store from '../redux/store';
import Router from './Router';
import { UniversityStyles } from './UniversityStyles';
const Header = React.lazy(() => import('../components/Header'));
const UserHeader = React.lazy(() => import('../components/UserHeader'));
const ActionPopup = React.lazy(() => import('../components/popups/ActionPopup'));
const ActionPopupChild = React.lazy(() => import('../components/popups/ActionPopupChild'));

function noop() {}

if (process.env.NODE_ENV !== 'development') {
    // eslint-disable-next-line no-console
    console.log = noop;
    // eslint-disable-next-line no-console
    console.warn = noop;
    // eslint-disable-next-line no-console
    console.error = noop;
}

let styles = null;

const App = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { loaded: widgetLoaded } = useSelector((state) => state.widget);
    const account = useSelector((state) => state.account);
    const actionPopup = useSelector((state) => state.actionPopup);
    const isMobile = useIsMobile();
    const emptyUniPage =
        window.location.href.includes('/interaction/ambassador/sign-in') ||
        window.location.href.includes('/interaction/ambassador/sign-up') ||
        window.location.href.includes('/interaction/sign-in-redirect') ||
        window.location.href.includes('/interaction/app-stores');
    const isCarousel = window.location.href.includes('/carousel');
    const isInteraction = window.location.href.includes('/interaction');

    useEffect(() => {
        if (!emptyUniPage) {
            checkUrlData();
        } else {
            if (isTokenExist()) {
                if (window.location.href.includes('/interaction/ambassador/sign-up')) {
                    dispatch(loginSuccess(getToken()));
                } else {
                    const universityId = extractIdFromUrlState('universityId');
                    const groupUniversityId = extractIdFromUrlState('universityGroupId');

                    const requestData = groupUniversityId
                        ? { universityGroupId: groupUniversityId }
                        : universityId
                          ? { universityId }
                          : {};

                    store.dispatch({
                        type: ACCOUNT_LOGOUT,
                        payload: requestData,
                    });
                }
            }
            dispatch({
                type: WIDGET_LOAD,
            });
        }

        if (!emptyUniPage) {
            const universityId = getUniversityId();
            const uniGroup = isUniGroup();
            const utmData = queryString.parse(window.location.href.split('?')[1]);
            const customStyles = [
                universityId ? (uniGroup ? 'group_' + universityId : universityId) : null,
                utmData.customStyle ? utmData.customStyle : null,
            ];
            const privateCode = window.location.href.split('dialog_code=')[1]
                ? window.location.href.split('dialog_code=')[1].split('&')[0]
                : null;

            const feedbackLikelyApply = new URL(window.location.href).searchParams.get('likelyApply');

            customStyles.filter(Boolean).map((style, index) => {
                const link = document.createElement('link');
                link.key = index;
                link.rel = 'preload';
                link.as = 'style';
                link.onload = () => {
                    link.onload = null;
                    link.rel = 'stylesheet';
                };
                link.type = 'text/css';
                link.href = `https://cdn.theaccessplatform.com/custom-styles/${style}.css`;
                document.head.appendChild(link);
            });
            if (sessionStorageWrapper.getItem('activePage')) {
                const activePage = JSON.parse(sessionStorageWrapper.getItem('activePage'));
                if (
                    activePage.universityId === universityId &&
                    uniGroup === activePage.isGroup &&
                    !feedbackLikelyApply
                ) {
                    if (activePage.page === 'liveStream') {
                        navigate(
                            `${getCommonRoutePath()}/${
                                activePage.institutionSlug ? `${activePage.institutionSlug}/` : ''
                            }live-streams/` + activePage.id
                        );
                    } else {
                        navigate(
                            `${getCommonRoutePath()}/${
                                activePage.institutionSlug ? `${activePage.institutionSlug}/` : ''
                            }interaction/dialogs/${activePage.id}${privateCode ? `?dialog_code=${privateCode}` : ''}`
                        );
                    }
                }
            }
        }

        NavigateWrapper.init(navigate); // TODO: Bug: navigate function changes every route change and it's not updated in NavigateWrapper
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []); // TODO: Bug: Dependency array is empty

    const checkUrlData = useCallback(() => {
        const universityId = getUniversityId();
        const uniGroup = isUniGroup();
        const queryData = queryString.parse(window.location.href.split('?')[1]);
        const onFinish = () => {
            if (queryData.pageTitle || queryData.pageDescription) {
                const { pageTitle, pageDescription } = queryData;
                dispatch(setTitles({ page_title: pageTitle, page_desc: pageDescription }));
            }
            if (queryData.profiles_title) {
                dispatch(setTitles({ profile_title: queryData.profiles_title }));
            }
            if (queryData.hide_filters === 'true') {
                dispatch(popcardHideFilters(true));
            }
        };
        let preFilterItems = [];
        if (queryData.prefilterItems && Array.isArray(queryData.prefilterItems)) {
            preFilterItems = queryData.prefilterItems;
        } else if (queryData.prefilterItems && typeof queryData.prefilterItems === 'string') {
            preFilterItems.push(queryData.prefilterItems);
        }
        window.addEventListener('message', function (event) {
            if (isGlobalVersionTwo && event.data && event.data.msgBody) {
                const data = event.data.msgBody;
                const details = data.personalDetails || {};
                const userInfo = {
                    email: data.email,
                    first_name: details.first_name,
                    last_name: details.last_name,
                    nationality: details.country_of_origin,
                    residence_country: details.current_location,
                    phone: details.contact_number,
                };
                dispatch({
                    type: SET_MULTI_DATA_REGISTER_MODAL,
                    payload: {
                        userInfo,
                    },
                });
            }
        });

        dispatch({
            type: WIDGET_INFO,
            payload: {
                university_id: uniGroup ? null : universityId,
                groupId: uniGroup ? universityId : null,
                preFilterItems: preFilterItems,
                searchType: queryData.searchType,
                showOnlyLiveEvents: !!queryData.onlyGroupLiveEvents,
                showOnlyGroupChats: !!queryData.onlyGroupChats,
                showOnlyUsers: !!queryData.onlyUsers,
                showOnlyPosts: !!queryData.onlyPosts,
                showOnlyFaqs: !!queryData.onlyFaqs,
            },
            onFinish: onFinish,
            onSuccess: afterSettingsReceived,
        });

        try {
            const utmData = queryString.parse(window.location.href.split('?')[1]);

            if (universityId?.length) {
                const utmNewData = {};
                if (utmData.utm_medium) {
                    utmNewData.utm_medium = utmData.utm_medium;
                }
                if (utmData.utm_source) {
                    if (!utmData.utm_medium) {
                        utmNewData.utm_medium = '(not set)';
                    }
                    utmNewData.utm_source = utmData.utm_source;
                } else if (document.referrer) {
                    if (!utmData.utm_medium) {
                        utmNewData.utm_medium = 'referral';
                    }
                    const url = document.referrer;
                    utmNewData.utm_source = url.match(/:\/\/(.[^/]+)/)[1];
                } else {
                    if (!utmData.utm_medium) {
                        utmNewData.utm_medium = '(none)';
                    }
                    utmNewData.utm_source = '(direct)';
                }

                if (utmData.utm_campaign) {
                    utmNewData.utm_campaign = utmData.utm_campaign;
                } else {
                    utmNewData.utm_campaign = '(direct)';
                }

                utmNewData.university_id = universityId;
                if (localStorageWrapper.getItem('utmData_' + universityId)) {
                    // first userCameFrom referrer most important
                    const cameFrom = JSON.parse(localStorageWrapper.getItem('utmData_' + universityId)).cameFrom;
                    if (cameFrom) {
                        utmNewData.cameFrom = cameFrom;
                    } else if (utmData.cameFrom) {
                        utmNewData.cameFrom = utmData.cameFrom;
                    }
                } else if (utmData.cameFrom && !isInIframe) {
                    utmNewData.cameFrom = window.location.href.split('?')[0];
                } else if (utmData.cameFrom) {
                    utmNewData.cameFrom = utmData.cameFrom;
                } else if (document.referrer.length) {
                    utmNewData.cameFrom = document.referrer;
                }

                if (
                    utmNewData.cameFrom &&
                    utmNewData.cameFrom.indexOf('cameFrom=') !== -1 &&
                    utmNewData.cameFrom.split('cameFrom=')[1]
                ) {
                    utmNewData.cameFrom = utmNewData.cameFrom.split('cameFrom=')[1].split('&')[0];
                }
                localStorageWrapper.setItem('utmData_' + universityId, JSON.stringify(utmNewData));
                dispatch({
                    type: SET_UTM_DATA,
                    payload: {
                        utmMedium: utmNewData.utm_medium,
                        utmSource: utmNewData.utm_source,
                        utmCampaign: utmNewData.utm_campaign,
                        cameFrom: utmNewData.cameFrom,
                    },
                });
            }
            // for copy button
            if (utmData.parentRef) {
                sessionStorageWrapper.setItem('parentRef', utmData.parentRef);
            }

            if (utmData.windowParentHeight) {
                sessionStorageWrapper.setItem('windowParentHeight', utmData.windowParentHeight);
            }

            const windowParentHeight = JSON.parse(sessionStorageWrapper.getItem('windowParentHeight'));

            if (utmData.windowParentHeight || windowParentHeight) {
                const height = utmData.windowParentHeight
                    ? +utmData.windowParentHeight
                    : windowParentHeight - (isMobile ? 18 : 80); // 80px margin of main body for desktop
                styles = document.createElement('style');
                styles.id = 'custom-interaction-styles';
                styles.innerHTML = `
                .interaction-page {
                  height: ${height}px;
                }
                `;
            }
        } catch (err) {
            // eslint-disable-next-line no-console
            console.log(err);
        }

        const isCarousel = window.location.href.includes('carousel');

        if (!isCarousel) {
            sessionStorageWrapper.removeItem('isCarousel');
            sessionStorageWrapper.removeItem('isCarouselAutoplayed');
        } else {
            const getCarouselAutoplayed = new URL(window.location.href).searchParams.get('autoplay');
            sessionStorageWrapper.setItem('isCarousel', isCarousel);
            sessionStorageWrapper.setItem('isCarouselAutoplayed', getCarouselAutoplayed);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []); // TODO: Bug: Dependency array is empty

    const afterSettingsReceived = useCallback(
        (settingsData) => {
            try {
                const uniGroup = isUniGroup();
                const universityId = getUniversityId();
                const queryData = queryString.parse(window.location.href.split('?')[1]);

                const isHomePage =
                    !window.location.pathname.split('/')[uniGroup ? 3 : 2] &&
                    !sessionStorageWrapper.getItem('activePage');
                const slugUni = settingsData.institution
                    ? window.location.pathname.split(settingsData.institution.slug)[1]
                    : null;
                // we can change request here with call to optimized api to get data which we need to know and which tabs to show in the header
                if (
                    !isHomePage &&
                    slugUni &&
                    slugUni.length > 1 &&
                    !window.location.href.includes('/carousel') &&
                    window.globalTapVersion !== 2
                ) {
                    dispatch({
                        type: MAIN_INFO,
                        payload: {
                            university_id: uniGroup ? null : universityId,
                            isDefault: true,
                        },
                    });
                }
                const authCode =
                    new URL(window.location.href).searchParams.get('auth') ||
                    new URL(window.location.href).searchParams.get('code');
                const urlToken = new URL(window.location.href).searchParams.get('access_token');
                const signedInToken = new URL(window.location.href).searchParams.get('signed_in_token');
                const signUp = new URL(window.location.href).searchParams.get('signUp');

                // External speaker data for live event
                const speakerAuthCode = new URL(window.location.href).searchParams.get('speakerAuthCode')
                    ? new URL(window.location.href).searchParams.get('speakerAuthCode')
                    : JSON.parse(sessionStorageWrapper.getItem('speakerAuthCode'));
                // Get Slug from University link
                const universityLinkSlug = new URL(window.location.href).searchParams.get('slug');

                // Get Stream Slug from internal Tap Feed link
                const internalLinkStreamSlug = new URL(window.location.href).pathname.includes('live-streams/')
                    ? new URL(window.location.href).pathname.split('/').pop()
                    : null;

                if (queryData.searchTerms) {
                    if (typeof queryData.searchTerms === 'string') {
                        dispatch(setSearchTerm(queryData.searchTerms));
                    } else {
                        queryData.searchTerms.map((term) => dispatch(setSearchTerm(term)));
                    }
                }

                const streamSlug = universityLinkSlug || internalLinkStreamSlug;
                if (queryData.leadType) {
                    const { path, action } = getLeadTypePath({
                        type: queryData.leadType,
                        groupChatId: queryData.leadGroupChatId,
                        groupChatPrivateCode: queryData.leadGroupChatPrivateCode,
                        ambassadorId: queryData.leadAmbassadorId,
                        isAutorized: !!account.token,
                    });

                    if (action === 'startRegistration') {
                        dispatch(
                            startRegistration({
                                university: { id: universityId },
                                id: queryData.leadAmbassadorId,
                                name: '',
                                type: 'ambassador',
                            })
                        );
                    }

                    navigate(`${getCommonRoutePath()}${path}`);
                }

                if ((authCode && !signUp && !streamSlug) || (authCode && signUp && !account.token)) {
                    authByCode(authCode.split('&')[0]);
                } else if (urlToken) {
                    authByToken(urlToken);
                } else if (signedInToken) {
                    authBySignedInToken(signedInToken, streamSlug);
                } else if (speakerAuthCode) {
                    authBySpeakerCode(speakerAuthCode, navigate, streamSlug, universityLinkSlug, isMobile);
                    sessionStorageWrapper.setItem('speakerAuthCode', JSON.stringify(speakerAuthCode));
                } else if (account.token) {
                    dispatch(getAccountInfo({ notifications: !streamSlug }));
                    if (streamSlug) {
                        navigate(`${getCommonRoutePath()}/live-streams`);
                    }
                }

                if (!account.token && streamSlug && !speakerAuthCode && !signedInToken) {
                    navigate(`${getCommonRoutePath()}/live-streams`);
                    dispatch({
                        type: SET_SLUG_FROM_URL,
                        payload: streamSlug,
                    });
                } else if (account.token && streamSlug) {
                    if (isMobile && isInIframe) {
                        dispatch({
                            type: SET_SLUG_FROM_URL,
                            payload: streamSlug,
                        });
                    } else {
                        navigate(`${getCommonRoutePath()}/live-streams/${streamSlug}`);
                    }
                }

                if (!uniGroup) {
                    dispatch({
                        type: SOCIAL_PROOF,
                        payload: {
                            university_id: universityId,
                        },
                    });
                }
                const prevUniTap = localStorageWrapper.getItem('prevUniTap');

                // after we find solution for GA we probably should change here condition on window.newTapUser
                if ((prevUniTap && prevUniTap + '' !== universityId + (uniGroup ? '_group' : '')) || !prevUniTap) {
                    const testBucket = getAbTestBucket(settingsData.ABTesting, ABTestNamesEnum.ABTEST_1);

                    plausibleEventHandler(PlausibleEventType.Landed, { testBucket });

                    localStorageWrapper.setItem('prevUniTap', universityId + (uniGroup ? '_group' : ''));
                }
            } catch (err) {
                // TODO handle error?
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [account, navigate, dispatch] // TODO: Bug: Dependency array is not exhaustive
    );

    const authByCode = useCallback(
        (authCode) => {
            const onSuccess = (token) => {
                dispatch({
                    type: LOGIN_SUCCESS,
                    payload: {
                        token: token,
                    },
                });

                setToken(token);

                dispatch(getAccountInfo({ authCode, notifications: true }));

                // don't think we need it anymore
                /* if (window.location.href.split("?auth=")[1]) {
                navigate({ pathname: window.location.pathname });
            } */
            };

            dispatch({
                type: AUTH_BY_CODE,
                payload: {
                    authCode,
                },
                onSuccess: onSuccess,
            });
        },
        [dispatch]
    );

    const authByToken = useCallback(
        (token) => {
            dispatch({
                type: LOGIN_SUCCESS,
                payload: {
                    token: token,
                    isLoading: true,
                },
            });

            setToken(token);

            dispatch(getAccountInfo({ notifications: true }));

            dispatch({
                type: MOB_APP_AUTH,
            });
        },
        [dispatch]
    );

    const authBySignedInToken = useCallback(
        (token, streamSlug) => {
            dispatch({
                type: LOGIN_SUCCESS,
                payload: {
                    token: token,
                    isLoading: true,
                },
            });

            setToken(token);

            dispatch(getAccountInfo({ notifications: !streamSlug }));

            if (streamSlug) {
                navigate(`${getCommonRoutePath()}/live-streams/${streamSlug}`);
            }
        },
        [dispatch, navigate]
    );

    const handleClosePopup = useCallback(
        (child) => {
            dispatch({
                type: child ? CLOSE_ACTION_MODAL_CHILD : CLOSE_ACTION_MODAL,
            });
        },
        [dispatch]
    );

    const isExternalSpeakerRegistration = useMemo(() => {
        !account.account_info && new URL(window.location.href).searchParams.get('dataRequired');
    }, [account]);

    if (account.isLoading || !widgetLoaded) {
        return <Loader />;
    }

    return (
        <>
            <MatomoScript />
            <UserWayScript />
            <TapPageVarSnippets />
            <div className={isExternalSpeakerRegistration ? 'external-speaker-registration' : ''}>
                {document.head.append(UniversityStyles())}
                {styles && document.head.append(styles)}
                {account.account_info && account.account_info.key === 'prospect' ? (
                    <>
                        {(isCarousel && isInteraction) || !isCarousel ? (
                            !window.location.href.includes('/interaction/sign-in-success') ? (
                                <Suspense fallback={null}>
                                    <UserHeader isMobile={isMobile} />
                                </Suspense>
                            ) : null
                        ) : null}
                    </>
                ) : (
                    <>
                        {!isCarousel &&
                        !isGlobalVersionTwo &&
                        !window.location.href.includes('/interaction/sign-in-success') &&
                        !window.location.href.includes('/interaction/ambassador/sign-up') ? (
                            <Suspense fallback={null}>
                                <Header />
                            </Suspense>
                        ) : null}
                    </>
                )}
                <Router />

                {actionPopup.isOpened && (
                    <Modal
                        open={actionPopup.isOpened}
                        styles={{ body: { padding: 0, ...actionPopup.bodyStyles } }}
                        centered={true}
                        footer={null}
                        closable={false}
                        maskClosable={true}
                        onCancel={handleClosePopup}
                        destroyOnClose
                        className={actionPopup.class || ''}
                    >
                        <Suspense fallback={null}>
                            <ActionPopup
                                title={actionPopup.title}
                                body={actionPopup.body}
                                // eslint-disable-next-line react/no-children-prop
                                children={actionPopup.children}
                                handleClose={handleClosePopup}
                            />
                        </Suspense>
                    </Modal>
                )}

                {actionPopup.withChild.isOpened && (
                    <Modal
                        open={true}
                        styles={{ body: { padding: 0, ...actionPopup.bodyStyles } }}
                        centered={!actionPopup.positionAbsolute}
                        footer={null}
                        closable={actionPopup.closable}
                        maskClosable={actionPopup.openOnMaskClick}
                        onCancel={() => handleClosePopup(true)}
                        destroyOnClose
                        className={'main-action-modal ' + (actionPopup.class || '')}
                    >
                        <Suspense fallback={null}>
                            <ActionPopupChild styles={actionPopup.styles} handleClose={() => handleClosePopup(true)}>
                                {actionPopup.withChild.children}
                            </ActionPopupChild>
                        </Suspense>
                    </Modal>
                )}
            </div>
        </>
    );
};

export default function AppWrapper() {
    return (
        <Provider store={store}>
            <AppProvider>
                <App />
            </AppProvider>
        </Provider>
    );
}
